// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

export interface Provider {
  id: string;
  name: string;
  code: string;
  country: string;
  isActive: boolean;
}

export interface BrokerageProvider {
  id: string;
  brokerageId: string;
  providerId: string;
  provider: {
    id: string;
    name: string;
    code: string;
    description?: string;
    logoUrl?: string;
    websiteUrl?: string;
    contactEmail?: string;
    contactPhone?: string;
    isActive?: boolean;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
  };
  isEnabled: boolean;
  customConfig?: Record<string, unknown>;
  policyTypeCount: number;
  enabledPolicyTypeCount: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  agencyCode?: string;
}

export interface PolicyType {
  id: string;
  providerId: string;
  providerName: string;
  name: string;
  code: string;
  category: 'LIFE' | 'HEALTH' | 'PROPERTY' | 'AUTO' | 'OTHER';
  isActive: boolean;
}

export interface BrokeragePolicyType {
  id: string;
  brokerageId: string;
  policyTypeId: string;
  policyType: {
    id: string;
    providerId: string;
    providerName: string;
    providerCode: string;
    name: string;
    code: string;
    description?: string;
    category: 'INSURANCE' | 'INVESTMENT' | 'PENSION' | 'OTHER';
    defaultTemplateId?: string;
    defaultTemplateName?: string;
    metadata?: Record<string, unknown>;
    isActive?: boolean;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
  };
  isEnabled: boolean;
  customTemplateId?: string;
  customTemplateName?: string;
  usingDefaultTemplate?: boolean;
  customConfig?: Record<string, unknown>;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export const brokerageConfigApi = {
  /**
   * Get enabled providers for the current brokerage
   */
  getEnabledProviders: async (
    brokerageId: string,
    isEnabled: boolean = true,
    page: number = 0,
    size: number = 1000
  ): Promise<PageResponse<BrokerageProvider>> => {
    const response = await api.get<PageResponse<BrokerageProvider>>(
      `/brokerages/${brokerageId}/providers`,
      {
        params: { isEnabled, page, size, sort: 'provider.name,asc' }
      }
    );
    return response.data;
  },

  /**
   * Get enabled policy types for the current brokerage
   */
  getEnabledPolicyTypes: async (
    brokerageId: string,
    providerId?: string,
    isEnabled: boolean = true,
    page: number = 0,
    size: number = 1000
  ): Promise<PageResponse<BrokeragePolicyType>> => {
    const url = providerId
      ? `/brokerages/${brokerageId}/policy-types/provider/${providerId}`
      : `/brokerages/${brokerageId}/policy-types`;

    const response = await api.get<PageResponse<BrokeragePolicyType>>(url, {
      params: {
        isEnabled,
        page,
        size,
        sort: 'policyType.name,asc'
      }
    });
    return response.data;
  }
};
